import { useCallback } from "react"
import { useClient } from "urql"

import { useProductFormImagesStore } from "../stores/useProductFormImagesStore"
import type { SignedUrl } from "../types"
import { IMAGE_ORDER } from "../types"

import type { FieldGroupEnum } from "@/graphql/codegen/graphql"
import { GetFileInfoDocument, ReferenceTableName } from "@/graphql/codegen/graphql"
import { useImageUpload } from "@/hooks/useImageUpload"
import { aggregateProductFormSignedUrls } from "@/screens/Products/routeLoader"

export const useProductFileHandling = (
  productId: string,
  versionId?: number
): {
  previewFiles: Partial<Record<FieldGroupEnum, { file: File; previewUrl: string } | null>>
  uploading: Partial<Record<FieldGroupEnum, boolean>>
  handleFileSelect: (type: FieldGroupEnum, file: File) => void
  handleSaveFiles: () => Promise<SignedUrl[]>
  handleCancel: (fieldGroup?: FieldGroupEnum) => void
} => {
  const { uploadFile } = useImageUpload({
    referenceTableName: ReferenceTableName.ProductVersion,
  })

  const client = useClient()

  const {
    previewFiles,
    setPreviewFile,
    clearUnsavedPreview,
    clearUnsavedPreviews,
    markPreviewAsSaved,
    setUploading,
    uploading,
    signedUrls,
  } = useProductFormImagesStore()

  const handleFileSelect = useCallback(
    (type: FieldGroupEnum, file: File) => {
      if (!file) return
      setPreviewFile(type, file)
    },
    [setPreviewFile]
  )

  const handleCancel = useCallback(
    (fieldGroup?: FieldGroupEnum) => {
      if (fieldGroup) {
        clearUnsavedPreview(fieldGroup)
      } else {
        clearUnsavedPreviews()
      }
    },
    [clearUnsavedPreview, clearUnsavedPreviews]
  )

  const handleSaveFiles = useCallback(async () => {
    try {
      const uploadPromises = Object.entries(previewFiles)
        .filter(([_, preview]) => preview?.file)
        .map(async ([type, preview]) => {
          if (!preview?.file) return null

          setUploading(type as FieldGroupEnum, true)
          const success = await uploadFile(preview.file, type as FieldGroupEnum, versionId?.toString() ?? productId)

          if (!success) {
            setUploading(type as FieldGroupEnum, false)
            return null
          }

          setUploading(type as FieldGroupEnum, false)
          markPreviewAsSaved(type as FieldGroupEnum)
          return {
            url: "",
            file: {
              fieldGroup: type as FieldGroupEnum,
              fileName: preview.file.name,
            },
          }
        })

      const results = await Promise.all(uploadPromises)
      const hasUploads = results.some((result) => result !== null)

      if (hasUploads) {
        // Fetch the updated file information from the backend
        const fileInfoResult = await client
          .query(
            GetFileInfoDocument,
            {
              fileInfoInput: {
                referenceTableName: ReferenceTableName.ProductVersion,
                referenceId: versionId?.toString() ?? productId,
                fieldGroups: [...IMAGE_ORDER],
              },
            },
            { requestPolicy: "network-only" }
          )
          .toPromise()

        if (fileInfoResult.data?.fileUpload?.getFileInfo?.__typename === "GetFileInfoSuccess") {
          const signedUrls = aggregateProductFormSignedUrls(fileInfoResult.data.fileUpload.getFileInfo.signedUrls) || []
          return signedUrls.map((signedUrl) => ({
            url: signedUrl.url || "",
            file: {
              fieldGroup: signedUrl.file?.fieldGroup,
              fileName: signedUrl.file?.originalName,
              originalName: signedUrl.file?.originalName,
            },
          })) as SignedUrl[]
        }
      }

      // If no uploads or fetch failed, return existing signed URLs
      return signedUrls
    } catch (error) {
      console.error("Failed to upload files:", error)
      return []
    }
  }, [previewFiles, signedUrls, setUploading, uploadFile, versionId, productId, markPreviewAsSaved, client])

  return {
    previewFiles,
    uploading,
    handleFileSelect,
    handleSaveFiles,
    handleCancel,
  }
}
